import React, { useCallback, useEffect, useRef, useState } from "react";

import { Dialog } from "@in/component-library";
import { Grid, GridItem, Input, Text, VerticalSpacer } from "@innovationnorway/lighthouse-components";
import useSWR from "swr";
import { SearchOrganizationResult } from "../../../main/models/search-organization-result";
import debounce from "../../../utils/debounce";
import { accountSearch } from "../../apis/getAccountSearch";
import { getUserAccounts } from "../../apis/getUserAccounts";
import { useLayout } from "../../hooks/useLayout";
import LoaderSmall from "../loaderSmall/loaderSmall";
import "./accountSearchModal.scss";

interface AccountSearchModalProps {
  selectAccountFromSearchResult: (account: SearchOrganizationResult) => void;
  showModal: boolean;
  setShowModal: (showModal: boolean) => void;
}

const AccountSearchModal = ({ selectAccountFromSearchResult, showModal, setShowModal }: AccountSearchModalProps) => {
  const { translations } = useLayout();
  const resultLimit = 10;
  const { data: userAccounts } = useSWR(`userAccounts`, getUserAccounts);

  const [searchResults, setSearchResults] = useState<SearchOrganizationResult[]>();
  const [loading, setLoading] = useState<boolean>(false);
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    if (showModal) {
      dialogRef.current?.showModal();
    } else {
      dialogRef.current?.close();
    }
  }, [showModal]);

  const close = () => {
    setShowModal(false);
  };

  const search = async (searchString: string) => {
    if (!searchString || searchString.length < 3) return;
    setLoading(true);
    const result = await accountSearch(searchString);
    if (result) {
      setSearchResults(result.results);
    }
    setLoading(false);
  };

  const debouncedSearch = useCallback(debounce(search, 500), []);

  const selectAccount = (accountId: string) => {
    const account = searchResults?.find((result) => result.orgNumber === accountId);
    if (!account) return;
    selectAccountFromSearchResult(account);
  };

  const getSearchResultLabel = (count: number) => {
    if (count === 0) {
      return translations.searchOrganizationNoResultLabel;
    }
    if (count > 10) {
      return translations.searchOrganizationResultTruncatedLabel.replaceAll("{0}", resultLimit.toString());
    }
    return translations.searchOrganizationResultLabel.replace("{0}", count.toString());
  };

  return (
    <Dialog
      title={translations.chooseAccountAddCompanyTitle}
      ref={dialogRef}
      onClose={close}
      closeButton={{
        onClick: () => dialogRef.current?.close(),
        "aria-label": translations.close
      }}
    >
      <Grid className="account-search">
        <GridItem spanLarge={12} spanMedium={12} spanSmall={3}>
          <Text as="label">
            <Input
              placeholder={translations.chooseAccountFindCompanyLabel}
              onChange={(e) => debouncedSearch(e.currentTarget.value)}
            />
          </Text>
          <VerticalSpacer size="xs" />
          {searchResults && searchResults.length > 0 && (
            <Text as="div">{getSearchResultLabel(searchResults.length)}</Text>
          )}
        </GridItem>
        {loading ? (
          <LoaderSmall />
        ) : (
          searchResults &&
          searchResults.length > 0 && (
            <GridItem spanLarge={12} spanMedium={12} spanSmall={4}>
              <Grid className="search-result-header">
                <GridItem spanLarge={5} spanMedium={5} spanSmall={2}>
                  <Text as="span">{translations.chooseAccountCompanyText}</Text>
                </GridItem>
                <GridItem spanLarge={3} spanMedium={3} spanSmall={1}>
                  <Text as="span">{translations.organisationNumberShortLabel}</Text>
                </GridItem>
                <GridItem spanLarge={4} spanMedium={4} spanSmall={1}>
                  <Text as="span">{translations.postalCode}</Text>
                </GridItem>
              </Grid>
              <ul className="results-container">
                {searchResults?.slice(0, resultLimit).map((result) => {
                  const accountAlreadyAdded =
                    userAccounts == undefined ||
                    userAccounts.find((ua) => ua.account.publicId === result.orgNumber && ua.emailVerified);
                  return (
                    <li
                      key={result.orgNumber}
                      onClick={() => (!accountAlreadyAdded ? selectAccount(result.orgNumber) : null)}
                      className={`search-result-item ${accountAlreadyAdded ? "existing" : ""}`}
                      aria-label={translations.searchOrganizationAlreadyAddedLabel}
                    >
                      <Grid>
                        <GridItem spanLarge={5} spanMedium={5} spanSmall={2}>
                          <Text as="span">{result.name}</Text>
                          {accountAlreadyAdded && (
                            <Text className="existing-label" as="p">
                              {translations.searchOrganizationAlreadyAddedLabel}
                            </Text>
                          )}
                        </GridItem>
                        <GridItem spanLarge={3} spanMedium={3} spanSmall={1}>
                          <Text as="span">{result.orgNumber}</Text>
                        </GridItem>
                        <GridItem spanLarge={4} spanMedium={4} spanSmall={1}>
                          <Text as="span">{result.postalInformation}</Text>
                        </GridItem>
                      </Grid>
                    </li>
                  );
                })}
              </ul>
            </GridItem>
          )
        )}
      </Grid>
    </Dialog>
  );
};
export default AccountSearchModal;
